import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import findIndex from 'lodash/findIndex';
import compact from 'lodash/compact';

export const INITIAL = 'initial';
export const EDITING = 'editing';
export const COMPLETE = 'complete';

const StepWizard = ({ children, formData, updateFormData, collapsed }) => {
  const [steps, setSteps] = useState(
    React.Children.map(children, (_, index) => ({
      stepNumber: index + 1,
      state: index === 0 ? EDITING : INITIAL,
      containerRef: React.createRef(),
    })),
  );

  return React.Children.map(compact(children), (child, index) => {
    const step = {
      ...steps[index],
      state: collapsed ? INITIAL : steps[index].state,
    };

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const edit = useCallback(() => {
      const newSteps = [...steps];
      newSteps.forEach((step) => {
        if (step.state === EDITING) step.state = step.previousState;
      });
      newSteps[index] = {
        ...newSteps[index],
        state: EDITING,
        previousState: step.state,
      };
      setSteps(newSteps);

      const nextStepContainerElement = newSteps[index].containerRef.current;
      if (nextStepContainerElement)
        setTimeout(() => {
          // Give the render cycle some time to complete so that the scroll doesn't happen
          // before the nex step renders.
          // There will be a more reliable way to do this and we should
          // revisit once more of the steps are complete.
          nextStepContainerElement.scrollIntoView();
        }, 10);
    }, [index, step.state]);

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const getCurrentIndex = useCallback(() => findIndex(steps, (step) => step.state === EDITING), []);

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const getNextIndex = useCallback((currentIndex) => {
      const nextIndex = Math.min(currentIndex + 1, steps.length - 1);
      if (steps[nextIndex].state === COMPLETE) return getNextIndex(nextIndex);
      return nextIndex;
    });

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const editNextStep = useCallback(() => {
      const currentIndex = getCurrentIndex();
      const nextIndex = getNextIndex(currentIndex);
      const newSteps = [...steps];

      newSteps[nextIndex] = { ...newSteps[nextIndex], state: EDITING, previousState: newSteps[nextIndex].state };
      setSteps(newSteps);

      const nextStepContainerElement = newSteps[getNextIndex(index)].containerRef.current;
      if (nextStepContainerElement)
        setTimeout(() => {
          // Give the render cycle some time to complete so that the scroll doesn't happen
          // before the nex step renders.
          // There will be a more reliable way to do this and we should
          // revisit once more of the steps are complete.
          nextStepContainerElement.scrollIntoView();
        }, 10);
    }, [getCurrentIndex, getNextIndex, index]);

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const completeStep = useCallback(
      (data) => {
        const newSteps = [...steps];
        updateFormData(data);
        newSteps[index].state = COMPLETE;
        setSteps(newSteps);
        editNextStep();
      },
      [editNextStep, index],
    );

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const hasState = useCallback((...states) => states.includes(step.state), [step.state]);

    const stepProps = {
      ...step,
      formData,
      completeStep,
      updateFormData,
      edit,
      editNextStep,
      hasState,
    };

    return React.cloneElement(child, { step: stepProps });
  });
};

export default StepWizard;

StepWizard.propTypes = {
  formData: PropTypes.object.isRequired,
  updateFormData: PropTypes.func.isRequired,
};
